import classNames from "classnames";
import React, { FunctionComponent, useState } from "react";
import {
  default as _Form,
  Field,
  FieldTemplateProps,
  FormProps,
  Widget,
} from "react-jsonschema-form";
import {
  ArrowDownIconPath,
  ArrowUpIconPath,
  DeleteIconPath,
  Icon,
} from "../Icon/Icon";
import { Button } from "../Button/Button";
import { CustomInput } from "../CustomInput/CustomInput";
import { PlusCircleIconPath } from "../Icon/Icon";
import CustomSelect from "./CustomSelect";

type Props = FormProps<any> & {};

/**
 * CustomDescriptionField
 */
const CustomDescriptionField: Field = ({ description }: any) => {
  return <small className="form-text text-muted">{description}</small>;
};

/**
 * CustomFieldTemplate is a form row stateless component
 * https://react-jsonschema-form.readthedocs.io/en/latest/advanced-customization/#custom-descriptions
 */
const CustomFieldTemplate: React.FunctionComponent<FieldTemplateProps> = props => {
  const {
    id,
    classNames,
    displayLabel,
    label,
    rawHelp,
    help,
    required,
    description,
    errors,
    children,
  } = props;
  return (
    <div className={classNames}>
      {displayLabel && (
        <label htmlFor={id} className="form-input-label">
          {label}
          {required ? "*" : null}
        </label>
      )}

      {children}
      {displayLabel && description ? description : null}
      {errors}
      {rawHelp ? (
        <small className="form-text text-muted">{props.rawHelp}</small>
      ) : (
        help
      )}
    </div>
  );
};

const CustomBase: React.StatelessComponent<any> = props => {
  // Note: since React 15.2.0 we can't forward unknown element attributes, so we
  // exclude the "options" and "schema" ones here.
  if (!props.id) {
    console.log("No id for", props);
    throw new Error(`no id for props ${JSON.stringify(props)}`);
  }
  const {
    value,
    readonly,
    disabled,
    autofocus,
    onBlur,
    onFocus,
    schema,
    options,
    formContext,
    ...inputProps
  } = props;

  // If options.inputType is set use that as the input type
  if (options.inputType) {
    inputProps.type = options.inputType;
  } else if (!inputProps.type) {
    // If the schema is of type number or integer, set the input type to number
    if (schema.type === "number") {
      inputProps.type = "number";
      // Setting step to 'any' fixes a bug in Safari where decimals are not
      // allowed in number inputs
      inputProps.step = "any";
    } else if (schema.type === "integer") {
      inputProps.type = "number";
      // Since this is integer, you always want to step up or down in multiples
      // of 1
      inputProps.step = "1";
    } else {
      inputProps.type = "text";
    }
  }

  // If multipleOf is defined, use this as the step value. This mainly improves
  // the experience for keyboard users (who can use the up/down KB arrows).
  if (schema.multipleOf) {
    inputProps.step = schema.multipleOf;
  }

  const _onChange = ({ target: { value } }: any) => {
    return props.onChange(value === "" ? options.emptyValue : value);
  };

  const inputClassName = classNames("input-group", {
    "is-required": props.required,
  });

  return (
    <div className={inputClassName}>
      <input
        className="form-control"
        readOnly={readonly}
        disabled={disabled}
        autoFocus={autofocus}
        value={value == null ? "" : value}
        {...inputProps}
        onChange={_onChange}
        onBlur={onBlur && (event => onBlur(inputProps.id, event.target.value))}
        onFocus={
          onFocus && (event => onFocus(inputProps.id, event.target.value))
        }
      />
    </div>
  );
};

function ArrayFieldTitle({ TitleField, idSchema, title, required }: any) {
  if (!title) {
    return null;
  }
  const id = `${idSchema.$id}__title`;
  return <TitleField id={id} title={title} required={required} />;
}

function ArrayFieldDescription({
  DescriptionField,
  idSchema,
  description,
}: any) {
  if (!description) {
    return null;
  }
  const id = `${idSchema.$id}__description`;
  return <DescriptionField id={id} description={description} />;
}

function ArrayFieldTemplate(props: any) {
  return (
    <fieldset className={props.className} id={props.idSchema.$id}>
      <ArrayFieldTitle
        key={`array-field-title-${props.idSchema.$id}`}
        TitleField={props.TitleField}
        idSchema={props.idSchema}
        title={props.uiSchema["ui:title"] || props.title}
        required={props.required}
      />

      {(props.uiSchema["ui:description"] || props.schema.description) && (
        <ArrayFieldDescription
          key={`array-field-description-${props.idSchema.$id}`}
          DescriptionField={props.DescriptionField}
          idSchema={props.idSchema}
          description={
            props.uiSchema["ui:description"] || props.schema.description
          }
        />
      )}

      <div
        className="array-item-list"
        key={`array-item-list-${props.idSchema.$id}`}
      >
        {props.items && props.items.map((p: any) => DefaultArrayItem(p))}
      </div>

      {props.canAdd && (
        <AddButton
          onClick={props.onAddClick}
          disabled={props.disabled || props.readonly}
        />
      )}
    </fieldset>
  );
}

function DefaultArrayItem(props: any) {
  const btnStyle = {
    flex: 1,
    paddingLeft: 6,
    paddingRight: 6,
    fontWeight: "bold",
  };
  return (
    <div
      key={props.key}
      className={props.className}
      style={{ display: "flex", alignItems: "center" }}
    >
      <div style={{ flex: 1, marginRight: 8 }}>{props.children}</div>

      {props.hasToolbar && (
        <div className="array-item-toolbox">
          <div
            className="btn-group"
            style={{
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            {(props.hasMoveUp || props.hasMoveDown) && (
              <IconButton
                icon={ArrowUpIconPath}
                tabIndex="-1"
                style={btnStyle}
                disabled={props.disabled || props.readonly || !props.hasMoveUp}
                onClick={props.onReorderClick(props.index, props.index - 1)}
              />
            )}

            {(props.hasMoveUp || props.hasMoveDown) && (
              <IconButton
                icon={ArrowDownIconPath}
                tabIndex="-1"
                style={btnStyle}
                disabled={
                  props.disabled || props.readonly || !props.hasMoveDown
                }
                onClick={props.onReorderClick(props.index, props.index + 1)}
              />
            )}

            {props.hasRemove && (
              <IconButton
                color="danger"
                icon={DeleteIconPath}
                tabIndex="-1"
                style={btnStyle}
                disabled={props.disabled || props.readonly}
                onClick={props.onDropIndexClick(props.index)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const AddButton: React.FC<any> = props => {
  return <IconButton {...{ ...props, icon: PlusCircleIconPath }}></IconButton>;
};
const IconButton: React.FC<any> = ({ children, icon, ...props }) => {
  return (
    <Button {...props}>
      <Icon size={"1em"} icon={icon} className="mx-auto">
        {children}
      </Icon>
    </Button>
  );
};

function RadioWidget(props: any) {
  const {
    options,
    value,
    required,
    disabled,
    readonly,
    autofocus = false,
    onBlur,
    onFocus,
    onChange,
    id,
  } = props;
  // Generating a unique field name to identify this set of radio buttons
  const name = Math.random().toString();
  const { enumOptions, enumDisabled, inline } = options;
  // checked={checked} has been moved above name={name}, As mentioned in #349;
  // this is a temporary fix for radio button rendering bug in React, facebook/react#7630.
  return (
    <div className="field-radio-group" id={id}>
      {enumOptions.map((option: any, i: number) => {
        const checked = option.value === value;
        const itemDisabled =
          enumDisabled && enumDisabled.indexOf(option.value) != -1;
        const disabledCls =
          disabled || itemDisabled || readonly ? "disabled" : "";
        const radio = (
          <span>
            <CustomInput
              type="radio"
              id={id + "#" + option.value}
              checked={checked}
              name={name}
              required={required}
              value={option.value}
              disabled={disabled || itemDisabled || readonly}
              autoFocus={autofocus && i === 0}
              onChange={() => onChange(option.value)}
              onBlur={onBlur && (event => onBlur(id, event.target.value))}
              onFocus={onFocus && (event => onFocus(id, event.target.value))}
              label={option.label}
            />
          </span>
        );

        return inline ? (
          <label key={i} className={`radio-inline ${disabledCls}`}>
            {radio}
          </label>
        ) : (
          <div key={i} className={`radio ${disabledCls}`}>
            <label>{radio}</label>
          </div>
        );
      })}
    </div>
  );
}
/*
 * widgets maps react-jsonschema-form widgets to our own custom components
 */
const widgets = {
  // SelectWidget has multiple and options props which are not in Widget type...
  SelectWidget: CustomSelect as Widget,
  BaseInput: CustomBase as Widget,
  RadioWidget: RadioWidget as Widget,
};

/*
 * fields maps react-jsonschema-form fields to our own custom components
 */
const fields = {
  DescriptionField: CustomDescriptionField,
};

const defaultChild = (
  <button type="submit" className="btn btn-primary">
    Submit
  </button>
);

export const VSETHForm: FunctionComponent<Props> = props => {
  const [formData, setFormData] = useState({});

  return (
    <_Form
      formData={formData}
      onChange={d => setFormData(d.formData)}
      children={defaultChild}
      FieldTemplate={CustomFieldTemplate}
      ArrayFieldTemplate={ArrayFieldTemplate}
      {...props}
      widgets={{ ...props.widgets, ...widgets }}
      fields={{ ...props.fields, ...fields }}
    />
  );
};
